import { useMutation, useQueryClient } from "@tanstack/react-query";
import { communityApi } from "@bw/shared/api";
import { invalidateFeedQueries } from "@bw/shared/hooks";
import type { CommentResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import { INLINE_GUARDIAN_PROMPT } from "../../../common/guardian/denials";

/**
 * Comment-on-a-post mutation (Slice 3, WRITE). Posts
 * `createComment(postId, { content })` — a TOP-LEVEL comment this slice, so
 * parent_id is omitted (never a real id) — and, on success, invalidates three
 * slots so every surface that reads the new comment (or the bumped count)
 * REFETCHES the SERVER's truth:
 *   - `social.postComments(postId)` — the open thread, so the new comment row
 *     arrives as server truth.
 *   - `social.post(postId)` — the detail page reads `comment_count` from this
 *     slot, so the "N comments" counter stays truthful (exactly what useVote
 *     does for the post's `score`); otherwise the count is stale until a reload.
 *   - canonical Feed pages — their card-level comment count is invalidated.
 *
 * No optimistic update: the cache is NOT mutated before the server confirms — no
 * phantom comment row, no client-side count bump. A rejected create (403 /
 * network) leaves the thread + counts exactly as the server last reported them
 * and surfaces on `mutation.error` for the page to show honestly (and to retain
 * the typed content for a retry).
 *
 * Re-entrancy + the content guard (required + trimmed) are the page's concern;
 * this hook sends whatever content it is handed.
 */
export function useCreateComment(postId: string) {
  const queryClient = useQueryClient();

  return useMutation<CommentResponse, unknown, string>({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (content: string) =>
      communityApi.createComment(postId, { content }),
    onSuccess: () => {
      // The open thread refetches the new comment as server truth.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.social.postComments(postId),
      });
      // The post slot carries comment_count — refetch it so the "N comments"
      // counter on the detail page is not left stale (mirrors useVote/score).
      void queryClient.invalidateQueries({
        queryKey: queryKeys.social.post(postId),
      });
      invalidateFeedQueries(queryClient);
    },
  });
}
